// DESCRIPTION: Attendee details step for the booking wizard — first/last name, email, phone,
// DESCRIPTION: special requirements, a 24h-reminder toggle, and a terms agreement gate.

import React from 'react';
import { __ } from '@wordpress/i18n';
import { createInterpolateElement } from '@wordpress/element';
import { applyFilters } from '@wordpress/hooks';
import DynamicField from './DynamicField';

/**
 * Field keys rendered as bespoke JSX below. Listed here so the
 * `nemtly_booking.booking.attendee_fields` filter receives them as context
 * and developers avoid collisions when injecting custom fields.
 *
 * @type {string[]}
 */
const BUILT_IN_FIELD_KEYS = [
	'firstName',
	'lastName',
	'email',
	'phone',
	'notes',
	'reminder',
	'terms',
];

/**
 * AttendeeForm — collects attendee details for a booking.
 *
 * Field values live on `form`; the parent maps first/last name into the
 * `attendee_name` wire field and stows notes/reminder in `booking_meta`.
 * Additional fields injected via the `nemtly_booking.booking.attendee_fields`
 * filter are rendered with `<DynamicField>` and persisted under
 * `booking_meta.custom_fields`.
 *
 * @param {Object}   props
 * @param {Object}   props.form             Form values: { firstName, lastName, email, phone, notes, reminder, terms, ...custom }.
 * @param {Object}   props.errors           Validation errors keyed by field name.
 * @param {Function} props.onChange         Called with ( key, value ) when a field changes.
 * @param {boolean}  [props.disabled=false] Disable all inputs (e.g. during submission).
 *
 * @return {JSX.Element} The attendee form fields.
 */
const AttendeeForm = ( { form, errors = {}, onChange, disabled = false } ) => {
	const update = ( key, value ) => onChange( key, value );

	/**
	 * Filters the list of attendee field descriptors. The six built-in
	 * fields ( {@link BUILT_IN_FIELD_KEYS} ) are always rendered as bespoke
	 * JSX regardless of this filter's output; any additional descriptors
	 * are rendered with `<DynamicField>`.
	 *
	 * @param {Array}  fields Field descriptors: { key, type, label, required, options, placeholder }.
	 * @param {Object} ctx    Booking context.
	 */
	const fields = applyFilters( 'nemtly_booking.booking.attendee_fields', [], {
		form,
	} );
	const customFields = fields.filter(
		( field ) => ! BUILT_IN_FIELD_KEYS.includes( field.key )
	);

	// Legal page URLs come from the localized booking config (set in PHP via
	// the `nemtly_booking_terms_url` / `nemtly_booking_privacy_url` filters,
	// privacy defaulting to the site's WordPress privacy page). When a URL is
	// absent the phrase renders as plain text, so the gate never breaks.
	const cfg =
		( typeof window !== 'undefined' &&
			( window.nemtlyBookingAjax || window.nemtlyBookingTrigger ) ) ||
		{};
	const legal = cfg.legal || {};
	const termsUrl = legal.terms_url || '';
	const privacyUrl = legal.privacy_url || '';

	const agreement = createInterpolateElement(
		__(
			'I agree to the <terms>terms of service</terms> and <privacy>privacy policy</privacy>',
			'nemtly-booking'
		),
		{
			// Link text is injected by createInterpolateElement at runtime, so
			// jsx-a11y's static anchor-has-content check is a false positive here.
			terms: termsUrl ? (
				// eslint-disable-next-line jsx-a11y/anchor-has-content
				<a
					className="nb-link"
					href={ termsUrl }
					target="_blank"
					rel="noopener noreferrer"
				/>
			) : (
				<span />
			),
			privacy: privacyUrl ? (
				// eslint-disable-next-line jsx-a11y/anchor-has-content
				<a
					className="nb-link"
					href={ privacyUrl }
					target="_blank"
					rel="noopener noreferrer"
				/>
			) : (
				<span />
			),
		}
	);

	return (
		<div className="nb-field-grid">
			<div className="nb-field-grid nb-field-grid--2">
				<div className="nb-field">
					<label className="nb-field__label" htmlFor="nb-first-name">
						{ __( 'First name', 'nemtly-booking' ) }
						<span className="nb-field__req">*</span>
					</label>
					<input
						id="nb-first-name"
						autoComplete="given-name"
						className={ `nb-input${
							errors.firstName ? ' nb-input--error' : ''
						}` }
						value={ form.firstName || '' }
						onChange={ ( e ) =>
							update( 'firstName', e.target.value )
						}
						placeholder={ __( 'Jane', 'nemtly-booking' ) }
						disabled={ disabled }
						aria-invalid={ !! errors.firstName }
						aria-describedby={
							errors.firstName ? 'nb-first-name-err' : undefined
						}
					/>
					{ errors.firstName && (
						<span
							className="nb-field__error"
							id="nb-first-name-err"
						>
							{ errors.firstName }
						</span>
					) }
				</div>
				<div className="nb-field">
					<label className="nb-field__label" htmlFor="nb-last-name">
						{ __( 'Last name', 'nemtly-booking' ) }
						<span className="nb-field__req">*</span>
					</label>
					<input
						id="nb-last-name"
						autoComplete="family-name"
						className={ `nb-input${
							errors.lastName ? ' nb-input--error' : ''
						}` }
						value={ form.lastName || '' }
						onChange={ ( e ) =>
							update( 'lastName', e.target.value )
						}
						placeholder={ __( 'Smith', 'nemtly-booking' ) }
						disabled={ disabled }
						aria-invalid={ !! errors.lastName }
						aria-describedby={
							errors.lastName ? 'nb-last-name-err' : undefined
						}
					/>
					{ errors.lastName && (
						<span className="nb-field__error" id="nb-last-name-err">
							{ errors.lastName }
						</span>
					) }
				</div>
			</div>

			<div className="nb-field">
				<label className="nb-field__label" htmlFor="nb-email">
					{ __( 'Email address', 'nemtly-booking' ) }
					<span className="nb-field__req">*</span>
				</label>
				<input
					id="nb-email"
					type="email"
					autoComplete="email"
					className={ `nb-input${
						errors.email ? ' nb-input--error' : ''
					}` }
					value={ form.email || '' }
					onChange={ ( e ) => update( 'email', e.target.value ) }
					placeholder={ __( 'jane@example.com', 'nemtly-booking' ) }
					disabled={ disabled }
					aria-invalid={ !! errors.email }
					aria-describedby={
						errors.email ? 'nb-email-err' : 'nb-email-help'
					}
				/>
				{ errors.email ? (
					<span className="nb-field__error" id="nb-email-err">
						{ errors.email }
					</span>
				) : (
					<span className="nb-field__helper" id="nb-email-help">
						{ __(
							'Your confirmation is sent here',
							'nemtly-booking'
						) }
					</span>
				) }
			</div>

			<div className="nb-field">
				<label className="nb-field__label" htmlFor="nb-phone">
					{ __( 'Phone number', 'nemtly-booking' ) }
					<span className="nb-field__optional">
						{ __( 'Optional', 'nemtly-booking' ) }
					</span>
				</label>
				<input
					id="nb-phone"
					type="tel"
					autoComplete="tel"
					className={ `nb-input${
						errors.phone ? ' nb-input--error' : ''
					}` }
					value={ form.phone || '' }
					onChange={ ( e ) => update( 'phone', e.target.value ) }
					placeholder={ __( '+1 (555) 000–0000', 'nemtly-booking' ) }
					disabled={ disabled }
					aria-invalid={ !! errors.phone }
					aria-describedby={
						errors.phone ? 'nb-phone-err' : 'nb-phone-help'
					}
				/>
				{ errors.phone ? (
					<span className="nb-field__error" id="nb-phone-err">
						{ errors.phone }
					</span>
				) : (
					<span className="nb-field__helper" id="nb-phone-help">
						{ __( 'For reminders only', 'nemtly-booking' ) }
					</span>
				) }
			</div>

			<div className="nb-field">
				<label className="nb-field__label" htmlFor="nb-notes">
					{ __( 'Special requirements', 'nemtly-booking' ) }
					<span className="nb-field__optional">
						{ __( 'Optional', 'nemtly-booking' ) }
					</span>
				</label>
				<textarea
					id="nb-notes"
					className="nb-input"
					rows="3"
					value={ form.notes || '' }
					onChange={ ( e ) => update( 'notes', e.target.value ) }
					placeholder={ __(
						'Accessibility needs, dietary requirements, questions for the host…',
						'nemtly-booking'
					) }
					disabled={ disabled }
				/>
			</div>

			<div className="nb-toggle-row">
				<span className="nb-toggle-row__label" id="nb-reminder-label">
					{ __( 'Email me a reminder 24h before', 'nemtly-booking' ) }
				</span>
				<button
					type="button"
					className={ `nb-toggle${
						form.reminder !== false ? ' nb-toggle--on' : ''
					}` }
					onClick={ () =>
						update( 'reminder', form.reminder === false )
					}
					role="switch"
					aria-checked={ form.reminder !== false }
					aria-labelledby="nb-reminder-label"
					disabled={ disabled }
				>
					<span className="nb-toggle__thumb" />
				</button>
			</div>

			<div className="nb-checkbox-row">
				<button
					type="button"
					id="nb-terms"
					className={ `nb-checkbox${
						form.terms ? ' nb-checkbox--checked' : ''
					}` }
					onClick={ () => update( 'terms', ! form.terms ) }
					role="checkbox"
					aria-checked={ !! form.terms }
					aria-labelledby="nb-terms-label"
					aria-describedby={
						errors.terms ? 'nb-terms-err' : undefined
					}
					disabled={ disabled }
				/>
				<span id="nb-terms-label" className="nb-checkbox-row__text">
					{ agreement }
					<span className="nb-field__req">*</span>
				</span>
			</div>
			{ errors.terms && (
				<span className="nb-field__error" id="nb-terms-err">
					{ errors.terms }
				</span>
			) }

			{ customFields.map( ( field ) => (
				<DynamicField
					key={ field.key }
					field={ field }
					value={ form[ field.key ] }
					error={ errors[ field.key ] }
					onChange={ update }
					disabled={ disabled }
				/>
			) ) }
		</div>
	);
};

export default AttendeeForm;
